/**
 * Invoice clients (recipient address book) — API wrappers.
 *
 * Endpoints live under /invoices/clients so the URLs read naturally as
 * part of the invoicing module; this matches the backend grouping.
 */
import api from "./client";
import type {
  InvoiceClient,
  InvoiceClientCreate,
  InvoiceClientInvoiceRow,
  InvoiceClientRollup,
  InvoiceClientUpdate,
  InvoiceClientWithRollup,
} from "@/types/invoice";

export interface InvoiceClientListResponse {
  items: InvoiceClient[];
  total: number;
}

export async function listInvoiceClients(opts?: {
  includeArchived?: boolean;
  search?: string;
}): Promise<InvoiceClientListResponse> {
  const params: Record<string, string | boolean> = {};
  if (opts?.includeArchived) params.include_archived = true;
  if (opts?.search) params.search = opts.search;
  const { data } = await api.get("/invoices/clients", { params });
  return data;
}

export async function getInvoiceClient(
  id: string,
): Promise<InvoiceClientWithRollup> {
  const { data } = await api.get(`/invoices/clients/${id}`);
  return data;
}

export interface InvoiceClientInvoicesResponse {
  items: InvoiceClientInvoiceRow[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export async function listInvoicesForClient(
  id: string,
  page = 1,
  perPage = 20,
): Promise<InvoiceClientInvoicesResponse> {
  const { data } = await api.get(`/invoices/clients/${id}/invoices`, {
    params: { page, per_page: perPage },
  });
  return data;
}

export async function createInvoiceClient(
  body: InvoiceClientCreate,
): Promise<InvoiceClient> {
  const { data } = await api.post("/invoices/clients", body);
  return data;
}

export async function updateInvoiceClient(
  id: string,
  body: InvoiceClientUpdate,
): Promise<InvoiceClient> {
  const { data } = await api.put(`/invoices/clients/${id}`, body);
  return data;
}

export async function archiveInvoiceClient(
  id: string,
): Promise<{ message: string; id: string }> {
  const { data } = await api.delete(`/invoices/clients/${id}`);
  return data;
}

export async function unarchiveInvoiceClient(
  id: string,
): Promise<InvoiceClient> {
  const { data } = await api.post(`/invoices/clients/${id}/unarchive`);
  return data;
}

// Re-export the rollup type so consumers can import everything they
// need from this module without reaching into types/invoice.
export type { InvoiceClient, InvoiceClientRollup };
